import React, { useCallback, useRef, createRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { updateText, textboxAlignRespond } from 'actions/livefeed'
import { Rnd } from 'react-rnd';
import update from 'immutability-helper';
import { handleSetCurrentEntity } from 'components/pages/livefeed/Editor/EditorContainer/common/commonFn'
import { useOnClickOutside } from 'components/common/hooks'
import { livefeedStyle } from 'components/pages/livefeed/defaultstate';
import WebFont from 'webfontloader'

/*
Special rule for using react-rnd
If the entity has padding and border: for example padding 5
then onDragStop or onResizeStop will return x,y and width include padding + border
So,if the padding and border for are added for editor view only,
the actual value need to be calculated. ( minos 5 for x,y and minos 10 for width)
But the position value props and size set on react-rnd component need to plus 5 for x,y and 10 for width from state,.
###Special rule for using AutosizeInput
Width of the enity is controlled by AutosizeInput, not by react-rnd since I disabled resizing function
width returns from AutosizeInput is the actual width of the input content without border, padding, so let it be
*/
const TextContainer = (props) => {
    const { editorView, scaleMode, dispatch, livefeedSlides, slideCurrentIndex, stepNumberState } = props

    const { slideType, fontFamily, historyEntities, textContainer } = livefeedSlides[slideCurrentIndex];
    let history = historyEntities.slice();
    let currentEntity = history[stepNumberState];

    /* Let's generate dynamic Input Text, I use $splice on reducer to remove item from array
    check https://github.com/kolodny/immutability-helper on $splice section
   */

    // Here generate dynamic ID when textContainer change, here I mutate it on purpose
    textContainer.map((obj, i) => {
        obj.id = i
        return obj
    })

    const textWrapperRefs = useRef([createRef(), createRef()]); // use textWrapperRefs to detect click outside

    function updateLength(value) {
        textWrapperRefs.current = textWrapperRefs.current.splice(0, value);
        for (let i = 0; i < value; i++) {
            textWrapperRefs.current[i] = textWrapperRefs.current[i] || createRef();
        }
        textWrapperRefs.current = textWrapperRefs.current.map((item) => item || createRef());
    }

    // call every time textContainer length change
    useEffect(() => {
        updateLength(textContainer.length)
    }, [textContainer])

    return (
        <React.Fragment>
            {textContainer.map((objectText, index) =>
                <div ref={textWrapperRefs.current[index]} key={index}>
                    <InputText
                        scaleMode={scaleMode}
                        dispatch={dispatch}
                        textContainer={textContainer}
                        index={index} inputRefs={textWrapperRefs}
                        fontFamily={fontFamily} editorView={editorView}
                        currentEntity={currentEntity}
                        objectText={objectText}
                        slideType={slideType}
                        slideCurrentIndex={slideCurrentIndex}
                    />
                </div>
            )}
        </React.Fragment>
    )
}

export default TextContainer;


export const InputText = ({ scaleMode, slideCurrentIndex, dispatch, slideType, textContainer, fontFamily, inputRefs, editorView, currentEntity, objectText, index }) => {

    const [inputRefState, setInputRefState] = useState(null)
    const [spanInputRefState, setSpanInputRefState] = useState(null)

    const [isEditMode, setisEditMode] = useState(false) // default able to drag
    const enableResizing = {
        bottom: false, bottomLeft: false, bottomRight: false, left: false,
        right: false, top: false, topLeft: false, topRight: false
    }
    // Respond on inputWidth change
    useEffect(() => {
        if (!scaleMode) {
            // can not fetch GTWalsheimProRegular on google server.
            if (fontFamily !== undefined) {
                fontFamily !== "GTWalsheimProRegular" &&
                    WebFont.load({
                        google: {
                            families: [`${fontFamily}`]
                        },
                        active: () => {
                            // respond when font-family changes update all textboxes
                            textContainer.map((textBox) => {
                                let el = document.getElementById(`slide-${slideCurrentIndex}-span-text-${textBox.id}`)
                                if (el) {
                                    let newWidth = el.offsetWidth // get the width from the span
                                    if (textBox.textStyle.italic || textBox.textStyle.bold) {
                                        newWidth = el.offsetWidth + 1
                                    }
                                    if (newWidth !== textBox.width) {
                                        dispatch(textboxAlignRespond(textBox, newWidth)); // textboxAlignRespond(objectText,newWidth)
                                    }
                                }
                            })

                        },
                        timeout: 500
                    });
            }
        }
        // only need to watch on fontFamily
    }, [scaleMode, dispatch, fontFamily, index, textContainer, slideCurrentIndex])

    const textboxOnDragStop = useCallback((newPosition) => {

        let newWidth = spanInputRefState.offsetWidth // get the width from the span
        let { x, y } = newPosition
        // prevent duplicate dispatch
        if (objectText.left !== x || objectText.top !== y) {
            const newobjectText = update(objectText, {
                top: { $set: y },
                left: { $set: x },
                width: { $set: newWidth },
                align: { $set: "" },
                activeToolbarItems: { [0]: { align: { $set: "" } } }
            });
            dispatch(updateText(newobjectText));
        }
    }, [dispatch, spanInputRefState, objectText])

    useEffect(() => {
        if (!scaleMode) {
            inputRefState && inputRefState.setAttribute('disabled', true); // Disable input
            inputRefState && inputRefState.setAttribute('autocomplete', "off")
        }
    }, [scaleMode, inputRefState])

    function inputDoubleClick(e) {
        e.preventDefault();
        e.stopPropagation();
        setisEditMode(true)
        inputRefState &&
            (
                inputRefState.removeAttribute('disabled'), //Enable input;
                inputRefState.focus()
            )
    }
    useOnClickOutside(inputRefs.current[index], () => {
        if (!scaleMode) {
            setisEditMode(false) // enable dragging
            inputRefState && inputRefState.setAttribute('disabled', "disabled") //Disable input
        }
    });


    const inputOnchange = useCallback((e) => {
        if (isEditMode && !scaleMode) {
            spanInputRefState.innerText = e.target.value; // the hidden span takes the value of the input;
            let newWidth = spanInputRefState.offsetWidth // get the width from the span
            let newLeft;
            switch (objectText.align) {
                case "center":
                    newLeft = slideType === "summary" ? livefeedStyle.align.summaryCenter - newWidth / 2 : livefeedStyle.align.center - newWidth / 2
                    break;
                case "right":
                    newLeft = slideType === "summary" ? livefeedStyle.align.summaryRight - newWidth : livefeedStyle.align.right - newWidth
                    break;
                case "left":
                    newLeft = livefeedStyle.align.left;
                    break;
            }

            const newobjectText = update(objectText, {
                content: { $set: e.target.value },
                width: { $set: newWidth },
                left: { $set: newLeft !== undefined ? newLeft : objectText.left }
            });
            dispatch(updateText(newobjectText));
            dispatch(textboxAlignRespond(newobjectText, newWidth)); // textboxAlignRespond(objectText,newWidth)
        }
    }, [scaleMode, dispatch, isEditMode, objectText, slideType, spanInputRefState])

    const handleSetCurrentEntityCallback = useCallback(() => {
        handleSetCurrentEntity(currentEntity, objectText, dispatch)
    }, [currentEntity, dispatch, objectText])

    const inputStyle = {
        width: objectText.width,
        fontFamily: fontFamily,
        fontWeight: objectText.textStyle.bold ? 'bold' : 'unset',
        fontStyle: objectText.textStyle.italic ? 'italic' : 'unset',
        textDecoration: objectText.textStyle.underline ? 'underline' : 'unset',
        fontSize: objectText.size,
        color: objectText.color
    }
    return (
        <React.Fragment>
            {(editorView && !scaleMode) ?
                <Rnd
                    bounds={".livefeed-preview-wrapper"}
                    data-tooltip={`Double click to edit text`}
                    className={`entity-resizable entity-resizable_no_padding ${currentEntity.id === index ? 'input-entity-active' : ''}`}
                    resizeHandleWrapperClass={"resizers"}
                    enableResizing={enableResizing}
                    size={{ width: "auto", height: "auto" }}
                    position={{
                        x: objectText.left * (scaleMode ? 0.375 : 1) - 5, y: objectText.top * (scaleMode ? 0.375 : 1) - 6
                    }}
                    onDragStart={(e) => { e.preventDefault(); handleSetCurrentEntityCallback() }}
                    onDragStop={(e, d) => {
                        e.preventDefault()
                        let position = update(d, {
                            x: { $set: d.x + 5 },//plus padding+border
                            y: { $set: d.y + 6 }
                        })
                        textboxOnDragStop(position)
                    }}
                    disableDragging={isEditMode}
                >
                    <React.Fragment>
                        <div ref={inputRefs.current[index]}
                            draggable={false}
                            onDoubleClick={(e) => inputDoubleClick(e)}
                        >
                            <input
                                draggable={false}
                                style={{
                                    ...inputStyle,
                                    pointerEvents: `${isEditMode ? 'all' : 'none'}`,
                                    cursor: `${isEditMode ? 'text' : 'move'}`,
                                    height: 'auto'
                                }}
                                value={objectText.content}
                                name={objectText.id}
                                ref={(node) => setInputRefState(node)}
                                onChange={(e) => inputOnchange(e)} type="text" />
                            <span
                                id={`slide-${slideCurrentIndex}-span-text-${index}`}
                                style={{
                                    height: 36,
                                    display: 'inline-block',
                                    fontFamily: fontFamily,
                                    fontWeight: objectText.textStyle.bold ? 'bold' : 'unset',
                                    fontStyle: objectText.textStyle.italic ? 'italic' : 'unset',
                                    textDecoration: objectText.textStyle.underline ? 'underline' : 'unset',
                                    fontSize: objectText.size
                                }} ref={(node) => setSpanInputRefState(node)} className='measure'>{objectText.content}</span>
                        </div>
                    </React.Fragment >
                </Rnd>
                :
                <div className="entity-resizable entity-resizable_no_padding ">
                    <input
                        type="text"
                        draggable={false}
                        style={{
                            ...inputStyle,
                            zIndex: 1,
                            position: 'absolute',
                            height: 'auto',
                            left: objectText.left * (scaleMode ? 0.375 : 1) - 5,
                            top: objectText.top * (scaleMode ? 0.375 : 1) - 6,
                            pointerEvents: 'none',
                            cursor: `${isEditMode ? 'text' : 'move'}`
                        }}
                        value={objectText.content}
                        name={objectText.id}
                        readOnly />
                </div>
            }
        </React.Fragment >
    );
}
InputText.propTypes = {
    fontFamily: PropTypes.string.isRequired,
    textContainer: PropTypes.array.isRequired,
    editorView: PropTypes.bool.isRequired,
    currentEntity: PropTypes.object.isRequired,
    objectText: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    inputRefs: PropTypes.any.isRequired,
    dispatch: PropTypes.func.isRequired
}


// WEBPACK FOOTER //
// ./src/components/pages/livefeed/Editor/EditorContainer/LivefeedPreview/LeftPaneView/entity/TextContainer.jsx